import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Avatar,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Tab,
  Tabs,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  LinearProgress,
  Badge,
  Snackbar,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoIcon,
  Notifications as NotificationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { apiFetch } from '../utils/apiFetch.js';

const EnhancedMoverProfile = ({ token }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: true,
    autoAcceptBookings: false,
    showAvailability: true,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch real mover profile from API
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const headers = {};
        if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
        const res = await apiFetch('/api/movers/me', { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const mc = data.moverCompany || data.mover || data;
        const user = data.user || {};
        // Map server fields to the shape the UI expects
        setProfile({
          id: mc._id,
          companyName: mc.name || mc.companyName || '',
          ownerName: user.name || mc.ownerName || '',
          email: user.email || mc.email || '',
          phone: mc.phone || user.phone || '',
          address: mc.address || '',
          businessNumber: mc.businessNumber || '',
          licenseNumber: mc.licenseNumber || '',
          rating: mc.averageRating || mc.rating || 0,
          totalJobs: mc.totalJobs || 0,
          yearsInBusiness: mc.yearsInBusiness || 0,
          authorized: Boolean(mc.authorized),
          specialties: mc.specialties || mc.services || [],
          certifications: mc.certifications || [],
          avatar: mc.profileImage || mc.avatar || null,
          coverImage: mc.coverImage || null,
          description: mc.description || '',
          operatingHours: mc.operatingHours || {},
          serviceAreas: mc.locations || mc.serviceAreas || [],
          lastUpdated: mc.updatedAt || mc.createdAt,
        });
        // Fetch notification settings too
        const notifRes = await apiFetch('/api/settings/notifications', {
          headers,
        });
        if (notifRes.ok) {
          const notifData = await notifRes.json();
          setSettings(s => ({ ...s, ...notifData }));
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setSnackbar({ open: true, message: 'Failed to load profile.', severity: 'error' });
      }
    };
    fetchProfile();
  }, [token]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
      // Map UI profile shape back to server shape
      const payload = {
        name: profile.companyName,
        phone: profile.phone,
        address: profile.address,
        businessNumber: profile.businessNumber,
        licenseNumber: profile.licenseNumber,
        description: profile.description,
        locations: profile.serviceAreas,
        services: profile.specialties,
        operatingHours: profile.operatingHours,
        yearsInBusiness: profile.yearsInBusiness,
      };
      const res = await apiFetch('/api/movers/me', {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setEditing(false);
      setSnackbar({ open: true, message: 'Profile saved successfully!', severity: 'success' });
    } catch (err) {
      console.error('Profile save error:', err);
      setSnackbar({ open: true, message: `Failed to save profile: ${err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    // Reset changes
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('profileImage', file);
      const headers = {};
      if (token && token !== 'cookie') headers.Authorization = `Bearer ${token}`;
      const res = await apiFetch('/api/movers/me/profile-image', {
        method: 'POST',
        headers,
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setProfile(prev => ({ ...prev, avatar: data.profileImage }));
      setSnackbar({ open: true, message: 'Profile photo updated.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to upload profile photo: ${err.message}`, severity: 'error' });
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleNotificationChange = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
      await apiFetch('/api/settings/notifications', {
        method: 'PUT',
        headers,
        body: JSON.stringify(updated),
      });
    } catch (err) {
      console.error('Notification settings save error:', err);
      // Revert on failure
      setSettings(s => ({ ...s, [key]: !value }));
      setSnackbar({ open: true, message: 'Failed to save notification settings.', severity: 'error' });
    }
  };

  const renderBasicInfo = () => (
    <Grid container spacing={isMobile ? 2 : 3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={{ textAlign: 'center', p: isMobile ? 2 : 3 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  component="label"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  size="small"
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? <CircularProgress size={16} color="inherit" /> : <PhotoIcon sx={{ fontSize: 16 }} />}
                  <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={handleProfileImageUpload} />
                </IconButton>
              }
            >
              <Avatar
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                src={profile?.avatar}
              >
                {profile?.companyName?.charAt(0)}
              </Avatar>
            </Badge>
            
            <Typography variant="h6" gutterBottom>
              {profile?.companyName}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Chip
                icon={<StarIcon />}
                label={`${profile?.rating} Rating`}
                color="primary"
                size="small"
              />
              {profile?.authorized && <Chip icon={<VerifiedIcon />} label="Verified" color="success" size="small" />}
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {profile?.totalJobs} completed jobs
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              {profile?.yearsInBusiness} years in business
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h6" gutterBottom>
              Specialties
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {profile?.specialties?.map((specialty, index) => (
                <Chip
                  key={index}
                  label={specialty}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 1.5, mb: 3 }}>
              <Typography variant="h6">Company Information</Typography>
              {!editing ? (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(true)}
                >
                  Edit Profile
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={profile?.companyName || ''}
                  disabled={!editing}
                  onChange={(e) => setProfile({...profile, companyName: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Owner Name"
                  value={profile?.ownerName || ''}
                  disabled={!editing}
                  onChange={(e) => setProfile({...profile, ownerName: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={profile?.email || ''}
                  disabled={!editing}
                  onChange={(e) => setProfile({...profile, email: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profile?.phone || ''}
                  disabled={!editing}
                  onChange={(e) => setProfile({...profile, phone: e.target.value})}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Address"
                  value={profile?.address || ''}
                  disabled={!editing}
                  multiline
                  rows={2}
                  onChange={(e) => setProfile({...profile, address: e.target.value})}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Description"
                  value={profile?.description || ''}
                  disabled={!editing}
                  multiline
                  rows={3}
                  onChange={(e) => setProfile({...profile, description: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Number"
                  value={profile?.businessNumber || ''}
                  disabled={!editing}
                  onChange={(e) => setProfile({...profile, businessNumber: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="License Number"
                  value={profile?.licenseNumber || ''}
                  disabled={!editing}
                  onChange={(e) => setProfile({...profile, licenseNumber: e.target.value})}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderBusinessSettings = () => (
    <Grid container spacing={isMobile ? 2 : 3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h6" gutterBottom>
              Operating Hours
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(profile?.operatingHours || {}).map(([day, hours]) => (
                <Grid item xs={12} key={day}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
                    <Typography sx={{ textTransform: 'capitalize', minWidth: 80 }}>
                      {day}:
                    </Typography>
                    <TextField
                      size="small"
                      value={hours}
                      disabled={!editing}
                      sx={{ maxWidth: isMobile ? '100%' : 200 }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h6" gutterBottom>
              Service Areas
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {profile?.serviceAreas?.map((area, index) => (
                <Chip
                  key={index}
                  label={area}
                  onDelete={editing ? () => {} : undefined}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            {editing && (
              <TextField
                fullWidth
                size="small"
                placeholder="Add new service area"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    // Add new service area
                  }
                }}
              />
            )}
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h6" gutterBottom>
              Business Settings
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoAcceptBookings}
                    onChange={(e) => setSettings({...settings, autoAcceptBookings: e.target.checked})}
                  />
                }
                label="Auto-accept bookings"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showAvailability}
                    onChange={(e) => setSettings({...settings, showAvailability: e.target.checked})}
                  />
                }
                label="Show availability to customers"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderNotificationSettings = () => (
    <Grid container spacing={isMobile ? 2 : 3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h6" gutterBottom>
              Notification Preferences
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                  />
                }
                label="Email notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.smsNotifications}
                    onChange={(e) => handleNotificationChange('smsNotifications', e.target.checked)}
                  />
                }
                label="SMS notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.marketingEmails}
                    onChange={(e) => handleNotificationChange('marketingEmails', e.target.checked)}
                  />
                }
                label="Marketing emails"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

    </Grid>
  );

  const tabData = [
    { label: 'Profile', icon: <PersonIcon />, content: renderBasicInfo },
    { label: 'Business', icon: <BusinessIcon />, content: renderBusinessSettings },
    { label: 'Notifications', icon: <NotificationIcon />, content: renderNotificationSettings },
  ];

  if (!profile) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 2, md: 4 }, bgcolor: '#F5F7FA', minHeight: '100%' }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3.5 }, mb: { xs: 2, md: 3 }, borderRadius: { xs: 2, md: 3 }, color: '#fff', background: 'linear-gradient(120deg, #102A43 0%, #176B87 100%)', boxShadow: '0 12px 28px rgba(16,42,67,0.18)' }}>
        <Typography variant="overline" sx={{ color: '#9FE3D0', fontWeight: 800, letterSpacing: 1.5 }}>PARTNER PROFILE</Typography>
        <Typography variant="h4" sx={{ fontWeight: 850, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>Company Profile</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.76)', mt: 0.5, display: { xs: 'none', sm: 'block' } }}>Manage the business details customers and MOVD operations rely on.</Typography>
      </Paper>

      {isMobile ? (
        <Stack spacing={1}>
          {tabData.map((tab, index) => (
            <Accordion key={tab.label} expanded={activeTab === index} onChange={(_, expanded) => setActiveTab(expanded ? index : activeTab)} disableGutters elevation={0} sx={{ border: '1px solid #D9E2EC', borderRadius: 2, overflow: 'hidden', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fff', px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.icon}
                  <Typography variant="subtitle1" fontWeight={800}>{tab.label}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1.5, bgcolor: '#F5F7FA' }}>
                {tab.content()}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      ) : (
        <Paper elevation={0} sx={{ width: '100%', border: '1px solid #D9E2EC', borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="standard"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {tabData.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                sx={{ minHeight: 60 }}
              />
            ))}
          </Tabs>
          <Box sx={{ p: 3 }}>
            {tabData[activeTab]?.content()}
          </Box>
        </Paper>
      )}

      {editing && (
        <Alert 
          severity="info" 
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          }
        >
          You have unsaved changes. Don't forget to save your updates!
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnhancedMoverProfile;